import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import api, { imgSrc } from "@/lib/api";
import { SectionHeading, revealProps } from "@/components/lux";
import Lightbox from "@/components/Lightbox";

function TiltImage({ item, onClick, index }) {
  const ref = useRef(null);
  const [t, setT] = useState({ rx: 0, ry: 0 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setT({ rx: -py * 8, ry: px * 8 });
  };
  return (
    <motion.button
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setT({ rx: 0, ry: 0 })}
      onClick={onClick}
      {...revealProps((index % 6) * 0.05)}
      className="group relative w-full mb-4 sm:mb-5 overflow-hidden rounded-xl bg-[#181B24] border border-white/5 tilt-card break-inside-avoid block"
      style={{ transform: `perspective(900px) rotateX(${t.rx}deg) rotateY(${t.ry}deg)` }}
      data-testid={`portfolio-item-${index}`}
    >
      <img
        src={imgSrc(item, 700)}
        alt={item.alt_text || item.title}
        loading="lazy"
        decoding="async"
        className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute bottom-0 left-0 p-4 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
        <p className="font-mono2 text-[10px] tracking-[0.25em] text-[#02AACF] uppercase">{item.category}</p>
        <p className="text-white font-display font-semibold text-sm mt-0.5">{item.title}</p>
      </div>
    </motion.button>
  );
}

export default function PortfolioSection() {
  const [categories, setCategories] = useState([]);
  const [allImages, setAllImages] = useState([]);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/portfolio/images").then((r) => setAllImages(r.data)).catch(() => {});
  }, []);

  const present = new Set(allImages.map((i) => i.category));
  const tabs = ["All", ...categories.map((c) => c.name).filter((n) => present.has(n))];
  const images = active === "All" ? allImages : allImages.filter((i) => i.category === active);

  return (
    <section id="portfolio" className="relative py-24 sm:py-32 bg-[#090A0F]" data-testid="portfolio-section">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-8">
        <motion.div {...revealProps()}>
          <SectionHeading
            eyebrow="Selected Work"
            title="The Portfolio"
            subtitle="Real people. Real moments. Real energy — captured across the islands and beyond."
          />
        </motion.div>

        <div className="mt-10 flex gap-2 sm:gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="portfolio-filters">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => setActive(t)}
              data-testid={`filter-${t.toLowerCase()}`}
              className={`whitespace-nowrap font-mono2 text-xs tracking-[0.15em] uppercase px-4 py-2.5 rounded-full border transition-all ${
                active === t
                  ? "bg-[#02AACF] border-[#02AACF] text-white"
                  : "border-white/15 text-white/60 hover:border-white/40 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-8 columns-2 md:columns-3 lg:columns-4 gap-4 sm:gap-5">
          {images.map((item, i) => (
            <TiltImage key={item.id} item={item} index={i} onClick={() => setLightbox(i)} />
          ))}
        </div>
        {images.length === 0 && (
          <p className="text-center text-[#64748B] py-20 font-mono2 text-sm">No images in this category yet.</p>
        )}
      </div>

      <Lightbox images={images} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
    </section>
  );
}
